// https://mui.com/x/react-date-pickers/quickstart/#theme-augmentation
// @ts-ignore
import type {} from '@mui/x-date-pickers/themeAugmentation'
import Fade from '@mui/material/Fade'
import { createTheme } from '@mui/material/styles'
import borders from './base/borders'
import shadows from './base/shadows'
import boxShadows from './base/boxShadows'
import colors from './base/colors'
import globals from './base/globals'
import typography from './base/typography'
import button from './components/button/index'
import dateRangePicker from './components/daterangepicker/daterangepicker'
import datePicker from './components/datepicker/datepicker'
import dialogActions from './components/dialog/dialogActions'
import dialogContent from './components/dialog/dialogContent'
import dialogTitle from './components/dialog/dialogTitle'
import link from './components/link/link'
import stepConnector from './components/stepper/stepConnector'
import stepLabel from './components/stepper/stepLabel'
import tableCell from './components/table/tableCell'
import tableRow from './components/table/tableRow'
import tooltip from './components/tooltip/tooltip'
import boxShadow from './functions/boxShadow'
import hexToRgb from './functions/hexToRgb'
import linearGradient from './functions/linearGradient'
import pxToRem from './functions/pxToRem'
import rgba from './functions/rgba'

export default createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1180,
      lg: 1366,
      xl: 1536,
      xxl: 1920,
    },
  },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  palette: { ...colors },
  borders: { ...borders },
  shadows: { ...shadows },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...dateRangePicker,
        ...datePicker,
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
    MuiButton: { ...button },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiDialogActions: { ...dialogActions },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiDialogTitle: { ...dialogTitle },
    MuiLink: { ...link },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiTableCell: { ...tableCell },
    MuiTableRow: { ...tableRow },
    MuiTooltip: { ...tooltip },

    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
      styleOverrides: {
        root: {
          ':empty': {
            display: 'none',
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.primary.main,
            opacity: 0.5,
          },
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.primary.main,
            opacity: 0.5,
          },
        }),
      },
    },
    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 16,
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.borders.borderRadius['lg'],
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 4,
      },
    },
    MuiPopover: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 8,
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.borders.borderRadius['lg'],
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderLeft: '4px solid',
          borderLeftColor: theme.palette.info.main,
          ...(ownerState.severity === 'success' && {
            borderLeftColor: theme.palette.success.main,
          }),
          ...(ownerState.severity === 'warning' && {
            borderLeftColor: theme.palette.warning.main,
          }),
          ...(ownerState.severity === 'error' && {
            borderLeftColor: theme.palette.error.main,
          }),
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: '2px solid',
          borderColor: theme.palette.darkblue[5],
          borderRadius: theme.borders.borderRadius['md'],
          backgroundColor: theme.palette.darkblue[5],
          padding: '3px 8px 4px 8px',
          '&.Mui-focused': {
            outline: '3px solid',
            outlineColor: theme.palette.darkblue[5],
            borderColor: theme.palette.primary.lighter,
          },
          '&.Mui-readOnly': {
            color: theme.palette.darkblue[50],
          },
          'label + &': {
            marginTop: '0.125rem !important',
          },
          '& .MuiChip-root': {
            margin: '0 2px 0 0',
          },
        }),
        input: {
          padding: 0,
          '&:focus': {
            background: 'none',
          },
        },
      },
    },
    MuiPickersInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: '2px solid',
          borderColor: theme.palette.darkblue[5],
          borderRadius: theme.borders.borderRadius['md'],
          backgroundColor: theme.palette.darkblue[5],
          padding: '0 8px 1px 8px',
          '&.Mui-focused': {
            outline: '3px solid',
            outlineColor: theme.palette.darkblue[5],
            borderColor: theme.palette.primary.lighter,
          },
          'label + &': {
            marginTop: '0.125rem !important',
          },
          '& .MuiChip-root': {
            margin: '0 2px 0 0',
          },
        }),
        input: {
          padding: 0,
          '&:focus': {
            background: 'none',
          },
        },
      },
    },
    MuiPickersSectionList: {
      styleOverrides: {
        root: {
          padding: '3px 0 !important',
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 8,
          },
        },
      },
      styleOverrides: {
        inputRoot: ({ ownerState, theme }) => ({
          color: theme.palette.primary.main,
          padding: '3px 8px 4px 8px',
          '& .MuiInput-input': {
            padding: 0,
            borderRadius: 0,
          },
          ...(ownerState.size === 'small' && {
            fontSize: '0.75rem',
            padding: '0 8px',
          }),
        }),
        tag: {
          margin: '0 2px 0 0',
        },
        listbox: ({ theme }) => ({
          '& .MuiAutocomplete-option': {
            ...theme.typography['p3'],
            color: theme.palette.primary.main,
            '&.Mui-focused': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity),
            },
            '&[aria-selected="true"]': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity),
              '&.Mui-focusVisible': {
                backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity + theme.palette.action.focusOpacity),
              },
            },
            '&[aria-selected="true"].Mui-focused': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
            },
            '&.Mui-focusVisible': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.focusOpacity),
            },
          },
          '& .MuiAutocomplete-option + .MuiAutocomplete-option': {
            marginTop: '1px',
          },
        }),
        noOptions: ({ theme }) => ({
          padding: '6px 16px',
          ...theme.typography['p3'],
          color: theme.palette.primary.light,
        }),
        paper: ({ theme }) => ({
          borderRadius: theme.borders.borderRadius['lg'],
        }),
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          position: 'relative',
          transform: 'none',
          transition: 'none',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['p6'],
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({theme}) => ({
          ...theme.typography['p3'],
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.primary.main,
            opacity: 0.5,
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          verticalAlign: 'top',
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                background: 'transparent',
                borderColor: theme.palette.darkblue[25],
              },
            },
            {
              props: { size: 'small' },
              style: {
                ...theme.typography['p5'],
                height: 23,
              },
            },
          ],
        }),
        deleteIcon: ({ theme }) => ({
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                color: theme.palette.darkblue[25],
                '&:hover': {
                  color: theme.palette.darkblue[50],
                },
              },
            },
          ],
        }),
      },
    },
    MuiMenu: {
      defaultProps: {
        disableAutoFocusItem: true,
      },
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['p3'],
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.hoverOpacity),
          },
          '&.Mui-selected': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity),
            '&.Mui-focusVisible': {
              backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity + theme.palette.action.focusOpacity),
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
          },
          '&.Mui-focusVisible': {
            backgroundColor: theme.alpha(theme.palette.secondary.main, theme.palette.action.focusOpacity),
          },
          '& + &': {
            marginTop: '1px',
          },
        }),
      },
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
        square: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-expanded:before': {
              opacity: 1,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          ':empty': {
            display: 'none',
          },
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.light,
          borderBottom: `2px solid ${theme.palette.primary.lighter as string}`,
        }),
      },
    },
    MuiSnackbar: {
      defaultProps: {
        slots: {
          transition: Fade,
        },
        autoHideDuration: 5000,
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.alpha(theme.palette.grey[900], 0.33),
          // redefine this variant styling, otherwise backgroundColor change above overrides it
          variants: [
            {
              props: { invisible: true },
              style: {
                backgroundColor: 'transparent',
              },
            },
          ],
        }),
      },
    },
  },
})
